import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Pill, TrendingUp, TrendingDown, History, Calendar } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAutoSync } from "../hooks/useAutoSync";
import { money, unitCost } from "../lib/format";
import { UNITS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const FREQ = [
  { v: "daily", l: "Daily" },
  { v: "specific_days", l: "Specific days" },
  { v: "occasional", l: "Occasional" },
];

function preview(f) {
  const price = parseFloat(f.price), servings = parseFloat(f.servings);
  const ss = parseFloat(f.serving_size), amt = parseFloat(f.amount_taken);
  if (isNaN(price) || isNaN(servings) || servings === 0) return {};
  const cps = price / servings;
  const perIntake = (!isNaN(amt) && !isNaN(ss) && ss !== 0) ? amt / ss : 1;
  const costIntake = cps * perIntake;
  let daily = null;
  if (f.frequency === "daily") daily = costIntake;
  else if (f.frequency === "specific_days") daily = costIntake * (parseFloat(f.days_per_week) || 0) / 7;
  else daily = costIntake * (parseFloat(f.times_per_month) || 0) / 30;
  return { cps, daily, weekly: daily * 7, monthly: daily * 30, yearly: daily * 365 };
}

const empty = {
  name: "", brand: "", price: "", container_size: "", container_unit: "grams",
  servings: "", serving_size: "", serving_unit: "grams", amount_taken: "",
  frequency: "daily", days_per_week: "", times_per_month: "", store: "", date: "", notes: "",
};

function SupplementDialog({ open, onOpenChange, supp, onSaved, cur }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      if (supp) setF({
        name: supp.name || "", brand: supp.brand || "", price: supp.price ?? "",
        container_size: supp.container_size ?? "", container_unit: supp.container_unit || "grams",
        servings: supp.servings ?? "", serving_size: supp.serving_size ?? "",
        serving_unit: supp.serving_unit || "grams", amount_taken: supp.amount_taken ?? "",
        frequency: supp.frequency || "daily", days_per_week: supp.days_per_week ?? "",
        times_per_month: supp.times_per_month ?? "", store: supp.store || "", date: supp.date || "", notes: supp.notes || "",
      });
      else setF(empty);
    }
  }, [open, supp]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const p = preview(f);

  const save = async () => {
    if (!f.name || !f.price || !f.servings) { toast.error("Name, price and servings are required"); return; }
    setSaving(true);
    const payload = {
      name: f.name, brand: f.brand, price: parseFloat(f.price),
      container_size: f.container_size ? parseFloat(f.container_size) : null,
      container_unit: f.container_unit, servings: parseFloat(f.servings),
      serving_size: f.serving_size ? parseFloat(f.serving_size) : null, serving_unit: f.serving_unit,
      amount_taken: f.amount_taken ? parseFloat(f.amount_taken) : null, frequency: f.frequency,
      days_per_week: f.days_per_week ? parseFloat(f.days_per_week) : null,
      times_per_month: f.times_per_month ? parseFloat(f.times_per_month) : null,
      store: f.store, date: f.date || null, notes: f.notes,
    };
    try {
      if (supp) await api.put(`/supplements/${supp.id}`, payload);
      else await api.post("/supplements", payload);
      toast.success(supp ? "Supplement updated" : "Supplement added");
      onOpenChange(false); onSaved && onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[480px]" data-testid="supplement-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{supp ? "Edit Supplement" : "Add Supplement"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input data-testid="supp-name-input" value={f.name} onChange={(e) => set("name")(e.target.value)} className="mt-1.5" placeholder="Creatine" autoFocus /></div>
            <div><Label>Brand</Label><Input value={f.brand} onChange={(e) => set("brand")(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Price *</Label><Input data-testid="supp-price-input" type="number" step="0.01" value={f.price} onChange={(e) => set("price")(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Container</Label><Input type="number" step="0.01" value={f.container_size} onChange={(e) => set("container_size")(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Unit</Label>
              <Select value={f.container_unit} onValueChange={set("container_unit")}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label># Servings *</Label><Input data-testid="supp-servings-input" type="number" step="0.01" value={f.servings} onChange={(e) => set("servings")(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Serving size</Label><Input type="number" step="0.01" value={f.serving_size} onChange={(e) => set("serving_size")(e.target.value)} className="mt-1.5" /></div>
            <div><Label>I take</Label><Input type="number" step="0.01" value={f.amount_taken} onChange={(e) => set("amount_taken")(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Frequency</Label>
              <Select value={f.frequency} onValueChange={set("frequency")}><SelectTrigger className="mt-1.5" data-testid="supp-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent></Select>
            </div>
            {f.frequency === "specific_days" && <div><Label>Days / week</Label><Input type="number" value={f.days_per_week} onChange={(e) => set("days_per_week")(e.target.value)} className="mt-1.5" /></div>}
            {f.frequency === "occasional" && <div><Label>Times / month</Label><Input type="number" value={f.times_per_month} onChange={(e) => set("times_per_month")(e.target.value)} className="mt-1.5" /></div>}
          </div>

          {p.cps != null && (
            <div className="rounded-xl bg-brand-light p-3 text-sm" data-testid="supp-cost-preview">
              <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-brand">
                <span>{money(p.cps, cur)}/serving</span>
                {p.daily != null && <span>{money(p.daily, cur)}/day</span>}
                {p.monthly != null && <span>{money(p.monthly, cur)}/mo</span>}
                {p.yearly != null && <span>{money(p.yearly, cur)}/yr</span>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Store / source</Label><Input value={f.store} onChange={(e) => set("store")(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Date purchased</Label><Input type="date" value={f.date} onChange={(e) => set("date")(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={f.notes} onChange={(e) => set("notes")(e.target.value)} className="mt-1.5" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="supp-save-button" className="w-full rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : supp ? "Save changes" : "Add supplement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuppPriceHistoryDialog({ supp, open, onOpenChange, cur }) {
  if (!supp) return null;
  const hist = supp.price_history || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="supp-history-dialog">
        <DialogHeader><DialogTitle className="font-display">{supp.name} — Price History</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {hist.length === 0 && <p className="text-sm text-slate-500">No purchases recorded yet.</p>}
          {hist.map((h, i) => {
            const prev = i > 0 ? hist[i - 1].cost_per_serving : null;
            let trend = null;
            if (prev != null && h.cost_per_serving != null) trend = h.cost_per_serving - prev;
            return (
              <div key={i} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3">
                <div>
                  <div className="font-mono text-sm font-semibold">{money(h.price, cur)}</div>
                  <div className="text-xs text-slate-500">{h.date} · {h.servings} servings{h.store ? ` · ${h.store}` : ""}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm">{h.cost_per_serving != null ? `${money(h.cost_per_serving, cur)}/serv` : "—"}</div>
                  {trend != null && trend !== 0 && (
                    <div className={`flex items-center justify-end gap-1 text-xs font-semibold ${trend > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {trend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {money(Math.abs(trend), cur)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddSuppPurchaseDialog({ supp, open, onOpenChange, onSaved, cur }) {
  const [price, setPrice] = useState("");
  const [container, setContainer] = useState("");
  const [servings, setServings] = useState("");
  const [store, setStore] = useState("");
  const [d, setD] = useState("");
  useEffect(() => {
    if (open && supp) {
      setPrice(""); setContainer(supp.container_size ?? ""); setServings(supp.servings ?? "");
      setStore(supp.store || ""); setD("");
    }
  }, [open, supp]);
  if (!supp) return null;
  const cps = (parseFloat(price) && parseFloat(servings)) ? parseFloat(price) / parseFloat(servings) : null;
  const save = async () => {
    if (!price) { toast.error("Enter a price"); return; }
    try {
      await api.post(`/supplements/${supp.id}/purchases`, {
        price: parseFloat(price), container_size: container ? parseFloat(container) : null,
        servings: servings ? parseFloat(servings) : null, store, date: d || null,
      });
      toast.success("New price logged");
      onOpenChange(false); onSaved && onSaved();
    } catch { toast.error("Could not save purchase"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="supp-add-purchase-dialog">
        <DialogHeader><DialogTitle className="font-display">Log New Price — {supp.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Old prices are kept. This becomes the active price.</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Price</Label><Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5" data-testid="supp-purchase-price-input" /></div>
          <div><Label>Container</Label><Input type="number" step="0.01" value={container} onChange={(e) => setContainer(e.target.value)} className="mt-1.5" /></div>
          <div><Label># Servings</Label><Input type="number" step="0.01" value={servings} onChange={(e) => setServings(e.target.value)} className="mt-1.5" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Store</Label><Input value={store} onChange={(e) => setStore(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Date</Label><Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="mt-1.5" /></div>
        </div>
        {cps != null && <div className="rounded-xl bg-brand-light p-2.5 text-sm font-mono text-brand">{money(cps, cur)}/serving</div>}
        <DialogFooter>
          <Button onClick={save} data-testid="supp-purchase-save-button" className="w-full rounded-full bg-brand hover:bg-brand-hover">Log price</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-1 font-mono text-lg font-extrabold">{value}</p>
    </div>
  );
}

export default function Supplements() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [supps, setSupps] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [histSupp, setHistSupp] = useState(null);
  const [purchaseSupp, setPurchaseSupp] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/supplements"); setSupps(data); }, []);
  useAutoSync(load, [load]);

  const del = async (id) => { await api.delete(`/supplements/${id}`); toast.success("Supplement deleted"); load(); };

  const monthPrefix = new Date().toISOString().slice(0, 7);
  const monthSpend = supps.filter((s) => (s.date || "").slice(0, 7) === monthPrefix).reduce((a, s) => a + (s.price || 0), 0);
  const projMonthly = supps.reduce((a, s) => a + (s.cost_per_month || 0), 0);
  const projAnnual = supps.reduce((a, s) => a + (s.cost_per_year || 0), 0);
  const mostServing = supps.reduce((m, s) => (s.cost_per_serving != null && (!m || s.cost_per_serving > m.cost_per_serving)) ? s : m, null);
  const mostMonth = supps.reduce((m, s) => (s.cost_per_month != null && (!m || s.cost_per_month > m.cost_per_month)) ? s : m, null);

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Supplements</h1>
          <p className="text-sm text-slate-500">Tracked separately from food</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-supplement-button"
          className="rounded-full bg-brand px-5 hover:bg-brand-hover"><Plus className="mr-1.5 h-4 w-4" />Add Supplement</Button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Stat label="Spent this month" value={money(monthSpend, cur)} />
        <Stat label="Projected monthly" value={money(projMonthly, cur)} />
        <Stat label="Projected annual" value={money(projAnnual, cur)} />
        <Stat label="Priciest / serving" value={mostServing ? `${money(mostServing.cost_per_serving, cur)}` : "—"} />
        <Stat label="Priciest / month" value={mostMonth ? `${money(mostMonth.cost_per_month, cur)}` : "—"} />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {supps.map((s) => (
          <div key={s.id} data-testid={`supplement-card-${s.id}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:border-brand/40 hover:shadow-lg hover:shadow-brand/5">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <h3 className="truncate font-display font-bold">{s.name}</h3>
                {s.brand && <p className="truncate text-xs text-slate-400">{s.brand}</p>}
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-100 text-violet-600"><Pill className="h-4 w-4" /></div>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Per serving</p>
                <p className="font-mono text-2xl font-bold text-brand" data-testid={`supp-serving-cost-${s.id}`}>{s.cost_per_serving != null ? money(s.cost_per_serving, cur) : "—"}</p>
                <p className="text-xs text-slate-500">{s.serving_size} {s.serving_unit} · {s.frequency}</p>
              </div>
              <div className="text-right text-xs font-mono text-slate-500">
                <p>{money(s.cost_per_day, cur)}/day</p>
                <p>{money(s.cost_per_month, cur)}/mo</p>
                <p>{money(s.cost_per_year, cur)}/yr</p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 border-t border-slate-100 pt-3">
              <button onClick={() => { setEditing(s); setOpen(true); }} data-testid={`edit-supp-${s.id}`}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" />Edit</button>
              <button onClick={() => setHistSupp(s)} data-testid={`history-supp-${s.id}`}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><History className="h-3.5 w-3.5" />History</button>
              <button onClick={() => setPurchaseSupp(s)} data-testid={`repurchase-supp-${s.id}`}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"><Plus className="h-3.5 w-3.5" />Price</button>
              <button onClick={() => del(s.id)} data-testid={`delete-supp-${s.id}`}
                className="ml-auto rounded-lg px-2 py-1 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {supps.length === 0 && (
        <div className="mt-16 text-center text-slate-400"><p className="text-sm">No supplements yet. Tap <span className="font-semibold text-brand">Add Supplement</span>.</p></div>
      )}

      <SupplementDialog open={open} onOpenChange={setOpen} supp={editing} onSaved={load} cur={cur} />
      <SuppPriceHistoryDialog supp={histSupp} open={!!histSupp} onOpenChange={(v) => !v && setHistSupp(null)} cur={cur} />
      <AddSuppPurchaseDialog supp={purchaseSupp} open={!!purchaseSupp} onOpenChange={(v) => !v && setPurchaseSupp(null)} onSaved={load} cur={cur} />
    </div>
  );
}
